'use client';

import { useCallback, useEffect, useState } from 'react';
import { getDb } from '@/lib/db';
import { STORAGE_UNAVAILABLE_MESSAGE, storageErrorMessage } from '@/lib/storageErrors';
import { RecurringExpense } from '@/lib/types';

export type RecurringExpensePayload = Omit<RecurringExpense, 'id' | 'createdAt' | 'updatedAt'>;

function notifyUpdate() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('budget-db-update'));
  }
}

function createId() {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useRecurringExpenses() {
  const [recurringExpenses, setRecurringExpenses] = useState<RecurringExpense[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const list = await db.recurringExpenses.orderBy('name').toArray();
      setRecurringExpenses(list);
      setError(null);
    } catch (err) {
      setError(storageErrorMessage('Impossible de charger les achats récurrents.', err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener('budget-db-update', handler);
    return () => window.removeEventListener('budget-db-update', handler);
  }, [load]);

  const addRecurringExpense = useCallback(async (payload: RecurringExpensePayload) => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      const now = new Date().toISOString();
      await db.recurringExpenses.add({
        ...payload,
        id: createId(),
        createdAt: now,
        updatedAt: now,
      });
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage("Impossible d'ajouter l'achat récurrent.", err));
    }
  }, []);

  const updateRecurringExpense = useCallback(async (id: string, payload: RecurringExpensePayload) => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      await db.recurringExpenses.update(id, { ...payload, updatedAt: new Date().toISOString() });
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage("Impossible de modifier l'achat récurrent.", err));
    }
  }, []);

  const deleteRecurringExpense = useCallback(async (id: string) => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      await db.recurringExpenses.delete(id);
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage("Impossible de supprimer l'achat récurrent.", err));
    }
  }, []);

  const resetData = useCallback(async () => {
    const db = getDb();
    if (!db) {
      setError(STORAGE_UNAVAILABLE_MESSAGE);
      return;
    }

    try {
      await db.recurringExpenses.clear();
      setError(null);
      notifyUpdate();
    } catch (err) {
      setError(storageErrorMessage('Impossible de réinitialiser les achats récurrents.', err));
    }
  }, []);

  return {
    recurringExpenses,
    loading,
    error,
    addRecurringExpense,
    updateRecurringExpense,
    deleteRecurringExpense,
    resetData,
  };
}
